/**
 * Canonical audio-lane identifiers for the AceLab / HypnoMates player.
 *
 * These are the SINGLE source of truth for lane names. Every lane reference in
 * the frontend (routing, ducking, priming, telemetry, preservation, decoding)
 * MUST use these constants instead of raw string literals, so that code written
 * by many agents stays consistent and a lane can never be silently renamed or
 * misspelled in one place.
 *
 * Naming rules (enforced by .github/instructions/acelab-lanes.instructions.md):
 *   - All lane identifiers are lowercase snake_case.
 *   - The main program / music / Drops deck is LANE_MUSIC_DROPS.
 *   - The foreground spoken hypnosis voice is LANE_HYPNOSIS_SPOKEN_MAIN.
 *   - Whisper lanes are LANE_WHISPER_LEFT / LANE_WHISPER_RIGHT.
 *   - Subliminal lanes are LANE_SUBLIMINAL_<LAYER#> (see below).
 *   - Dynamic spatial "encounter" lanes use the LANE_ENCOUNTER_PREFIX.
 */

export const LANE_MUSIC_DROPS = "music_drops";

export const LANE_HYPNOSIS_SPOKEN_MAIN = "hypnosis_spoken_main";

export const LANE_WHISPER_LEFT = "whisper_left";
export const LANE_WHISPER_RIGHT = "whisper_right";

// Subliminal lanes. The spoken one-shot subliminal is layer 1; the continuous
// background "sex bed" is layer 2. Add LANE_SUBLIMINAL_3, _4, ... as new
// subliminal layers are introduced. Keep the numeric suffix explicit so the
// count of subliminal lanes is always visible.
export const LANE_SUBLIMINAL_1 = "subliminal_1";
export const LANE_SUBLIMINAL_2 = "subliminal_2";

// The generic overlay lane (foreground ambient / non-subliminal one-shots).
export const LANE_OVERLAY = "overlay";

// Dynamic spatial "encounter" lanes are named LANE_ENCOUNTER_PREFIX + <id>.
export const LANE_ENCOUNTER_PREFIX = "encounter_";

// Convenience: every lane that is a subliminal layer (for startsWith checks).
export const LANE_SUBLIMINAL_PREFIX = "subliminal_";

// Convenience: every lane that is a whisper lane (for startsWith checks).
export const LANE_WHISPER_PREFIX = "whisper_";

// The full ordered set of fixed (non-dynamic) lanes, for iteration in
// preservation / teardown / telemetry loops.
export const ALL_FIXED_LANES = Object.freeze([
    LANE_MUSIC_DROPS,
    LANE_HYPNOSIS_SPOKEN_MAIN,
    LANE_OVERLAY,
    LANE_WHISPER_LEFT,
    LANE_WHISPER_RIGHT,
    LANE_SUBLIMINAL_1,
    LANE_SUBLIMINAL_2,
]);

// Helpers so callers never hand-write the prefix strings.
export function isSubliminalLane(laneName) {
    return String(laneName || "").startsWith(LANE_SUBLIMINAL_PREFIX);
}

export function isWhisperLane(laneName) {
    return String(laneName || "").startsWith(LANE_WHISPER_PREFIX);
}

export function isEncounterLane(laneName) {
    return String(laneName || "").startsWith(LANE_ENCOUNTER_PREFIX);
}
